// use client: branchType/search/location are local UI state and divRef drives a
// smooth scroll — the selected branch also swaps the map iframe's src, so the
// whole tab + list + map region is one interactive unit.
'use client'

import type { CmsBranch } from '@/utils/getGlobalChrome'

import { useRef, useState } from 'react'
import { Col, Nav, Row } from 'react-bootstrap'

import DownIcon from '@/assets/images/downIcon.svg'
import RightIcon from '@/assets/images/rightIcon.svg'
import SearchIcon from '@/assets/images/search.svg'
import { parseCmsHtml } from '@/utils'

interface BranchLocatorProps {
  branches: CmsBranch[]
  atmDescription?: string | null
}

function BranchLocator({ branches, atmDescription }: BranchLocatorProps) {
  const [location, setLocation] = useState<CmsBranch | undefined>(branches[0])
  const [branchType, setBranchType] = useState('WITHIN')
  const [search, setSearch] = useState('')
  const divRef = useRef<HTMLDivElement>(null)
  const scrollToBottom = () => {
    if (divRef.current) {
      divRef.current.scrollTo({
        top: divRef.current.scrollHeight,
        behavior: 'smooth' // This enables smooth scrolling
      })
    }
  }

  const filteredBranches = branches
    .filter(branch =>
      search.length >= 3
        ? (branch.address ?? '').toLowerCase().includes(search.toLowerCase())
        : true
    )
    .filter(branch => branch.type === branchType)

  return (
    <>
      <Nav
        variant="underline"
        className="d-flex justify-content-start py-5"
        style={{ color: '#000045' }}
      >
        <Nav.Link
          className={`nav-link ${branchType === `WITHIN` && `active`}`}
          onClick={() => setBranchType('WITHIN')}
        >
          Within Klang Valley
        </Nav.Link>
        <Nav.Link
          className={`nav-link ${branchType === `OUTSIDE` && `active`}`}
          onClick={() => setBranchType('OUTSIDE')}
        >
          Outside Klang Valley
        </Nav.Link>
        <Nav.Link
          className={`nav-link ${branchType === `ATM` && `active`}`}
          onClick={() => setBranchType('ATM')}
        >
          ATM Location
        </Nav.Link>
      </Nav>
      <Row>
        <Col md={4}>
          {branchType === 'ATM' ? (
            <p style={{ color: 'var(--alrajhi-black)' }}>{parseCmsHtml(atmDescription)}</p>
          ) : (
            <div className="input-group input-group-lg mb-3">
              <input
                type="text"
                className="form-control contact-us-search-input"
                placeholder="Search by city, area, or address"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <div
                className="input-group-text"
                style={{ backgroundColor: 'white', height: '48px' }}
              >
                <img src={SearchIcon.src} alt="" style={{ margin: '2.5px' }} />
              </div>
            </div>
          )}

          {/* Branch list */}
          {/* Scrollable region must be keyboard-focusable so keyboard users
                can scroll it (WCAG 2.1.1). role=region + label name it for SR.
                axe REQUIRES tabIndex here, so the jsx-a11y rule is a false
                positive in this specific case — suppressed intentionally. */}
          <div
            className="contact-map"
            ref={divRef}
            role="region"
            aria-label="Branch and Smart Financial Centre list"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- scrollable region must be focusable per WCAG 2.1.1
            tabIndex={0}
          >
            {filteredBranches.map(branch => (
              <div key={branch.id ?? branch.name} className="branch d-flex flex-row">
                <div style={{ color: '#000045' }}>
                  <button
                    type="button"
                    className="branch-select"
                    onClick={() => setLocation(branch)}
                  >
                    {branch.name}
                  </button>
                  <br />
                  <span
                    style={{ fontSize: '14px' }}
                    dangerouslySetInnerHTML={{ __html: branch.address ?? '' }}
                  />
                  <span
                    style={{
                      fontSize: '14px',
                      marginTop: 15,
                      display: 'block'
                    }}
                  >
                    Customer Care:{' '}
                    <a
                      href="tel:+60323326000"
                      className="branch-phone"
                      style={{ color: 'inherit' }}
                    >
                      {branch.phoneNo}
                    </a>
                  </span>
                </div>

                <div style={{ marginLeft: 'auto' }}>
                  <img src={RightIcon.src} alt="" />
                </div>
              </div>
            ))}
          </div>

          {/* Show arrow only if 4 or more */}

          {filteredBranches.length >= 4 && (
            <div
              className="d-flex justify-content-center mt-md-4 mt-2 mb-2 border-0 bg-transparent"
              style={{ cursor: 'pointer' }}
              onClick={scrollToBottom}
              aria-label="Scroll down"
            >
              <img src={DownIcon.src} alt="" />
            </div>
          )}
        </Col>

        <Col md={8}>
          <iframe
            title="map"
            src={location?.src ?? ''}
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0, height: '70vh', borderRadius: '15px' }}
            allowFullScreen
            sandbox="allow-same-origin allow-scripts allow-popups"
          ></iframe>
        </Col>
      </Row>
    </>
  )
}

export default BranchLocator
